import {
  ArrowLeftRight,
  Bot,
  Gauge,
  HardDrive,
  type LucideIcon,
  Mail,
  MessageSquare,
  Phone,
  Send,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import {
  DarkPopover,
  InfoButton,
  type InfoId,
  TitleWithInfo,
  popoverBody,
} from "./pricing-popovers";
import { T } from "./pricing-shared";

type CostRow = {
  readonly icon: LucideIcon;
  readonly id: InfoId;
  readonly title: string;
  readonly sub: ReactNode;
};

const TILE: CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 9,
  background: "#F2EFE8",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "none",
};

function Row({
  row,
  open,
  compact,
  onToggle,
  onClose,
}: {
  readonly row: CostRow;
  readonly open: boolean;
  readonly compact: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
}): ReactNode {
  const t = useTranslations("public.pricingCostCards");
  const Icon = row.icon;
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        gap: 13,
        alignItems: "center",
        minHeight: 58,
        borderTop: "1px solid rgba(11,15,25,0.07)",
      }}
    >
      <span style={TILE}>
        <Icon size={17} strokeWidth={1.7} color="#3F444D" aria-hidden="true" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <TitleWithInfo
          title={row.title}
          style={{
            display: "block",
            fontFamily: T.sans,
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.3,
            color: T.ink,
          }}
          info={
            <InfoButton
              open={open}
              onToggle={onToggle}
              label={t("mehrInfosZu", { title: row.title })}
            />
          }
        />
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 12.5,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.sub}
        </div>
      </div>
      {open ? (
        <DarkPopover
          onClose={onClose}
          width={compact ? "auto" : 352}
          position={
            compact ? { top: 52, left: 0, right: 0 } : { top: 52, left: 36 }
          }
        >
          {popoverBody(row.id)}
        </DarkPopover>
      ) : null}
    </div>
  );
}

function Card({
  icon,
  title,
  titleNote,
  badge,
  badgeColor,
  sub,
  rows,
  compact,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly titleNote: string | null;
  readonly badge: string;
  readonly badgeColor: string;
  readonly sub: string;
  readonly rows: readonly CostRow[];
  readonly compact: boolean;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const HeadIcon = icon;
  const badgeEl = (
    <span
      style={{
        display: "inline-block",
        fontFamily: T.label,
        fontSize: 9.5,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: badgeColor,
        border: "1px solid rgba(11,15,25,0.16)",
        borderRadius: 999,
        padding: "4px 9px",
        flex: "none",
        whiteSpace: "nowrap",
      }}
    >
      {badge}
    </span>
  );
  const subEl = (
    <p
      style={{
        fontFamily: T.sans,
        fontSize: 13.5,
        lineHeight: 1.5,
        color: "#8A8D94",
        margin: compact ? "5px 0 0" : "0 0 8px 51px",
      }}
    >
      {sub}
    </p>
  );
  return (
    <div
      style={{
        border: "1px solid rgba(11,15,25,0.12)",
        borderRadius: 13,
        background: "#FAFAFA",
        padding: compact ? "20px 18px 8px" : "24px 26px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: compact ? "flex-start" : "center",
          gap: 13,
          marginBottom: compact ? 14 : 6,
        }}
      >
        <span style={TILE}>
          <HeadIcon
            size={19}
            strokeWidth={1.7}
            color={badgeColor === T.accentDark ? T.accentDark : "#3F444D"}
            aria-hidden="true"
          />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: compact ? 20 : 22,
              lineHeight: 1.15,
              color: T.ink,
              margin: 0,
            }}
          >
            {title}
            {titleNote ? (
              <>
                {" "}
                <span
                  style={{
                    fontFamily: T.sans,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#8A8D94",
                    whiteSpace: "nowrap",
                  }}
                >
                  {titleNote}
                </span>
              </>
            ) : null}
          </h3>
          {compact ? (
            <>
              {subEl}
              <div style={{ marginTop: 9 }}>{badgeEl}</div>
            </>
          ) : null}
        </div>
        {compact ? null : badgeEl}
      </div>
      {compact ? null : subEl}
      {rows.map((row) => (
        <Row
          key={row.id}
          row={row}
          open={openInfo === row.id}
          compact={compact}
          onToggle={() => onToggle(row.id)}
          onClose={onClose}
        />
      ))}
    </div>
  );
}

/** The two "Keine versteckten Kosten" cards (desktop side-by-side / mobile stacked). */
export function CostCards({
  compact,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly compact: boolean;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const t = useTranslations("public.pricingCostCards");

  // Built here rather than at module level: every label needs the translator,
  // and a hook cannot run outside the component.
  const plainSub = (text: string): ReactNode => (
    <span style={{ color: "#575B63" }}>{text}</span>
  );
  const allowanceTags = {
    free: (chunks: ReactNode) => (
      <span style={{ color: T.accent }}>{chunks}</span>
    ),
    rest: (chunks: ReactNode) => (
      <span style={{ color: "#8A8D94" }}>{chunks}</span>
    ),
  };

  const externalRows: readonly CostRow[] = [
    {
      icon: Send,
      id: "epost",
      title: t("ePostVersandBriefmarken"),
      sub: plainSub(t("portoDruckSelbstkosten")),
    },
    {
      icon: Phone,
      id: "tel",
      title: t("festnetzMobiltarife"),
      sub: plainSub(t("ueberTwilioNummernPortierbar")),
    },
    {
      icon: Bot,
      id: "ki",
      title: t("kiNutzung"),
      sub: plainSub(t("ueberKiAnbieterSelbstkosten")),
    },
  ];

  const usageRows: readonly CostRow[] = [
    {
      icon: HardDrive,
      id: "datei",
      title: t("zusaetzlicherDateispeicher"),
      sub: t.rich("k100GbFrei10", allowanceTags),
    },
    {
      icon: Mail,
      id: "post",
      title: t("zusaetzlicheEMailPostfaecher"),
      sub: t.rich("k5Frei350", allowanceTags),
    },
    {
      icon: MessageSquare,
      id: "auf",
      title: t("kommentarSpeicher"),
      sub: t.rich("k50EinheitFrei10", allowanceTags),
    },
  ];

  return (
    <>
      <Card
        icon={ArrowLeftRight}
        title={t("externeKosten")}
        titleNote={t("fallenSowieso")}
        badge={t("selbstkosten")}
        badgeColor={T.accentDark}
        sub={t("verbrauchsabhaengigUeberDrittanbieter")}
        rows={externalRows}
        compact={compact}
        openInfo={openInfo}
        onToggle={onToggle}
        onClose={onClose}
      />
      <Card
        icon={Gauge}
        title={t("hoherNutzung")}
        titleNote={null}
        badge={t("selten")}
        badgeColor="#8A8D94"
        sub={t("grosszuegigInklusiveSeltenRelevant")}
        rows={usageRows}
        compact={compact}
        openInfo={openInfo}
        onToggle={onToggle}
        onClose={onClose}
      />
    </>
  );
}
